/**
 * Spatial layout for Grid AI automation.
 * Places newly added objects relative to anchors ("on top of", "left of", …)
 * using world-space bounding boxes so stacks sit correctly.
 */

import * as THREE from 'three';
import {
  getCommandSnippetForObjectType,
  inferColorFromCommand,
  parseRotationFromText,
} from './propertyHelpers.js';

const SHAPE_TYPES = [
  'cube',
  'sphere',
  'cylinder',
  'cone',
  'pyramid',
  'capsule',
  'torus',
  'rectangle',
  'ellipse',
  'triangle',
  'plane',
];

export const PLACEMENT_TYPES = [
  'on_top',
  'above',
  'below',
  'under',
  'left',
  'right',
  'front',
  'behind',
  'next_to',
];

const RELATION_PATTERNS = [
  {
    re: /\b([a-z0-9_]+)\s+on\s+top\s+of\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'on_top',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+above\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'above',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+(?:below|under|underneath)\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'below',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+(?:to\s+the\s+)?left\s+of\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'left',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+(?:to\s+the\s+)?right\s+of\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'right',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+in\s+front\s+of\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'front',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+behind\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'behind',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  {
    re: /\b([a-z0-9_]+)\s+(?:next\s+to|beside|alongside)\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'next_to',
    subjectIdx: 1,
    anchorIdx: 2,
  },
  // "add a cube with a cone on top"
  {
    re: /\b(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\s+with\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\s+on\s+top\b/i,
    placement: 'on_top',
    subjectIdx: 2,
    anchorIdx: 1,
  },
  // "stack a cone on a cube"
  {
    re: /\bstack\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\s+on\s+(?:a\s+|an\s+|the\s+)?([a-z0-9_]+)\b/i,
    placement: 'on_top',
    subjectIdx: 1,
    anchorIdx: 2,
  },
];

const SKIP_WORDS = new Set([
  'add',
  'create',
  'make',
  'put',
  'place',
  'the',
  'a',
  'an',
  'and',
  'with',
  'some',
  'to',
  'scene',
]);

function normalizeType(word) {
  const w = String(word || '')
    .toLowerCase()
    .replace(/s$/, ''); // cubes → cube (simple)
  // undo over-stem for "glass" etc. — only strip trailing s for known shapes
  const raw = String(word || '').toLowerCase();
  if (SHAPE_TYPES.includes(raw)) return raw;
  if (SHAPE_TYPES.includes(w)) return w;
  // lights / content
  const aliases = {
    cubes: 'cube',
    spheres: 'sphere',
    cylinders: 'cylinder',
    cones: 'cone',
    pyramids: 'pyramid',
  };
  return aliases[raw] || raw;
}

/**
 * Parse "cone on top of a cube" style relations from free text.
 * @returns {{ subject: string, anchor: string, placement: string } | null}
 */
export function parseSpatialRelation(userCommand) {
  const text = String(userCommand || '').trim();
  if (!text) return null;

  for (const pattern of RELATION_PATTERNS) {
    const m = text.match(pattern.re);
    if (!m) continue;
    const subject = normalizeType(m[pattern.subjectIdx]);
    const anchor = normalizeType(m[pattern.anchorIdx]);
    if (!subject || !anchor || subject === anchor) continue;
    if (SKIP_WORDS.has(subject) || SKIP_WORDS.has(anchor)) continue;
    return {
      subject,
      anchor,
      placement: pattern.placement,
    };
  }

  return null;
}

/**
 * Rewrite / inject tool calls so the anchor is created first and the subject
 * has relativeTo + placement. Safe no-op when no relation is detected.
 * If the command refers to "the cube" and that shape already exists, skip recreating it.
 */
export function enrichSpatialToolCalls(userCommand, toolCalls, scene = null) {
  const relation = parseSpatialRelation(userCommand);
  if (!relation || !Array.isArray(toolCalls)) return toolCalls;

  const { subject, anchor, placement } = relation;
  const calls = toolCalls.map((c) =>
    c && typeof c === 'object'
      ? { ...c, parameters: { ...(c.parameters || {}) } }
      : c
  );

  const isAddShape = (call, type) => {
    if (!call || call.name !== 'add_object') return false;
    const t = String(call.parameters?.objectType || '').toLowerCase();
    return t === type;
  };

  const refersToExisting = /\bthe\s+/i.test(String(userCommand || ''));
  const existingAnchor =
    scene && refersToExisting ? findAnchorObject(scene, anchor) : null;

  let anchorCall = existingAnchor
    ? null
    : calls.find((c) => isAddShape(c, anchor));
  let subjectCall = calls.find((c) => isAddShape(c, subject));

  if (!existingAnchor && !anchorCall) {
    anchorCall = {
      name: 'add_object',
      parameters: { objectType: anchor, quantity: 1 },
    };
  }
  if (!subjectCall) {
    subjectCall = {
      name: 'add_object',
      parameters: { objectType: subject, quantity: 1 },
    };
  }

  subjectCall.parameters = {
    ...subjectCall.parameters,
    objectType: subject,
    relativeTo: anchor,
    placement,
  };
  delete subjectCall.parameters.position;
  if (subjectCall.parameters.properties) {
    const props = { ...subjectCall.parameters.properties };
    delete props.position;
    subjectCall.parameters.properties = props;
  }

  if (anchorCall) {
    anchorCall.parameters = {
      ...anchorCall.parameters,
      objectType: anchor,
    };
    delete anchorCall.parameters.relativeTo;
    delete anchorCall.parameters.placement;
  }

  const others = calls.filter(
    (c) => c && !isAddShape(c, anchor) && !isAddShape(c, subject)
  );

  return anchorCall
    ? [anchorCall, subjectCall, ...others]
    : [subjectCall, ...others];
}

/**
 * Find the best anchor object in the scene for a type/name hint.
 * Prefers the most recently added matching shape.
 */
export function findAnchorObject(scene, typeOrName) {
  if (!scene || !typeOrName) return null;
  const want = String(typeOrName).toLowerCase().trim();
  if (!want) return null;

  const matches = [];
  scene.traverse((obj) => {
    if (!obj || (!obj.isObject3D && !obj.isMesh && !obj.isGroup)) return;
    if (obj.isScene) return;
    // Skip pure helpers / cameras / lights
    if (obj.isCamera || obj.isLight || obj.isHelper) return;

    const meshCode = String(
      obj.mesh_code || obj.userData?.mesh_code || obj.userData?.originalType || ''
    ).toLowerCase();
    const fileName = String(obj.file_name || obj.userData?.file_name || '').toLowerCase();
    const name = String(obj.name || '').toLowerCase();
    const type = String(obj.userData?.type || '').toLowerCase();

    const isShape = type === 'shape' || obj.isShapes || obj.glb_type === 'shapes';
    const codeMatch =
      meshCode === want ||
      meshCode === want.replace(/s$/, '') ||
      (want && meshCode.includes(want));
    const nameMatch =
      fileName === want ||
      fileName.startsWith(want) ||
      (fileName && fileName.includes(want));

    if (codeMatch || (isShape && nameMatch) || nameMatch) {
      matches.push(obj);
    }
  });

  if (!matches.length) return null;
  // Last match ≈ most recently added among traversed objects; prefer direct scene children order
  const children = scene.children || [];
  let best = matches[matches.length - 1];
  for (let i = children.length - 1; i >= 0; i--) {
    const child = children[i];
    if (matches.includes(child)) {
      best = child;
      break;
    }
    // nested
    const nested = matches.find((m) => child === m || child.children?.includes?.(m));
    if (nested) {
      best = nested;
      break;
    }
  }
  return best;
}

/**
 * Move `object` so it sits relative to `anchor` per placement.
 * Uses world bounding boxes (works for centered Three.js primitives).
 */
export function applyRelativePlacement(
  object,
  anchor,
  placement,
  options = {}
) {
  if (!object || !anchor || !object.position) return false;

  const gap = Number(options.gap);
  const clearGap = Number.isFinite(gap) ? gap : 0.02;

  // Ensure matrices are current before measuring
  anchor.updateMatrixWorld?.(true);
  object.updateMatrixWorld?.(true);

  const anchorBox = new THREE.Box3().setFromObject(anchor);
  const movingBox = new THREE.Box3().setFromObject(object);

  if (anchorBox.isEmpty() || movingBox.isEmpty()) return false;

  const anchorCenter = anchorBox.getCenter(new THREE.Vector3());
  const movingCenter = movingBox.getCenter(new THREE.Vector3());

  // Vector from object.position to its bbox center (handles centered geometries)
  const centerOffset = movingCenter.clone().sub(object.position);
  const bottomOffset = movingBox.min.y - object.position.y;
  const topOffset = movingBox.max.y - object.position.y;

  let targetPos = new THREE.Vector3();

  const rel = String(placement || 'on_top').toLowerCase();

  switch (rel) {
    case 'on_top':
    case 'above':
      // Rest subject's bottom on anchor's top
      targetPos.set(
        anchorCenter.x - centerOffset.x,
        anchorBox.max.y + clearGap - bottomOffset,
        anchorCenter.z - centerOffset.z
      );
      break;
    case 'below':
    case 'under':
      targetPos.set(
        anchorCenter.x - centerOffset.x,
        anchorBox.min.y - clearGap - topOffset,
        anchorCenter.z - centerOffset.z
      );
      break;
    case 'left':
      {
        const leftOffset = movingBox.max.x - object.position.x;
        targetPos.set(
          anchorBox.min.x - clearGap - leftOffset,
          anchorCenter.y - centerOffset.y,
          anchorCenter.z - centerOffset.z
        );
      }
      break;
    case 'right':
    case 'next_to':
      {
        const rightOffset = movingBox.min.x - object.position.x;
        targetPos.set(
          anchorBox.max.x + clearGap - rightOffset,
          anchorCenter.y - centerOffset.y,
          anchorCenter.z - centerOffset.z
        );
      }
      break;
    case 'front':
      {
        const frontOffset = movingBox.min.z - object.position.z;
        targetPos.set(
          anchorCenter.x - centerOffset.x,
          anchorCenter.y - centerOffset.y,
          anchorBox.max.z + clearGap - frontOffset
        );
      }
      break;
    case 'behind':
      {
        const backOffset = movingBox.max.z - object.position.z;
        targetPos.set(
          anchorCenter.x - centerOffset.x,
          anchorCenter.y - centerOffset.y,
          anchorBox.min.z - clearGap - backOffset
        );
      }
      break;
    default:
      targetPos.set(
        anchorCenter.x - centerOffset.x,
        anchorBox.max.y + clearGap - bottomOffset,
        anchorCenter.z - centerOffset.z
      );
  }

  object.position.copy(targetPos);
  object.updateMatrixWorld?.(true);
  return true;
}

/**
 * Normalize placement aliases from AI output.
 */
export function normalizePlacement(value) {
  const v = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  const map = {
    on_top: 'on_top',
    ontop: 'on_top',
    top: 'on_top',
    above: 'above',
    over: 'on_top',
    below: 'below',
    under: 'below',
    underneath: 'below',
    left: 'left',
    left_of: 'left',
    right: 'right',
    right_of: 'right',
    front: 'front',
    in_front: 'front',
    in_front_of: 'front',
    behind: 'behind',
    back: 'behind',
    next_to: 'next_to',
    nextto: 'next_to',
    beside: 'next_to',
    alongside: 'next_to',
  };
  return map[v] || (PLACEMENT_TYPES.includes(v) ? v : null);
}

// ─── Multi-object group layouts (line / grid / circle / stack) ─────────

const WORD_QUANTITIES = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  fifteen: 15,
  twenty: 20,
  thirty: 30,
};

const COUNTABLE_SHAPE_WORDS =
  'cubes?|spheres?|cylinders?|cones?|pyramids?|capsules?|tori|toruses?|rectangles?|ellipses?|triangles?|planes?|objects?|shapes?|models?|meshes?|lights?|texts?|hotspots?|boxes?';

const OBJECT_TYPE_SHAPE_ALT = SHAPE_TYPES.map((s) =>
  s === 'torus' ? 'tori|toruses?|torus' : `${s}s?`
).join('|');

function stripNonCountNumbers(text) {
  return String(text || '')
    .toLowerCase()
    // 90°, 90 deg, 90 degrees, 1.5 rad
    .replace(/-?\d+(?:\.\d+)?\s*(?:°|deg(?:rees?)?|radians?|rad)\b/gi, ' ')
    // rotated 90 / rotation: 90 / angle 45
    .replace(
      /\b(?:rotated?|rotation|angle|pitch|yaw|roll|tilt(?:ed)?)\s*[:=]?\s*-?\d+(?:\.\d+)?/gi,
      ' '
    )
    // 90 on x axis / 90 around y
    .replace(
      /\b-?\d+(?:\.\d+)?\s*(?:on|around|about|along)\s+(?:the\s+)?[xyz]\b/gi,
      ' '
    )
    .replace(/\b\d+(?:\.\d+)?%/g, ' ')
    .replace(/\b\d+\s*[x×]\s*\d+\b/gi, ' ');
}

function singularizeShapeWord(word) {
  const w = String(word || '').toLowerCase();
  if (!w) return null;
  if (w === 'tori' || w === 'toruses' || w === 'torus') return 'torus';
  if (w === 'boxes' || w === 'box') return 'cube';
  if (SHAPE_TYPES.includes(w)) return w;
  if (w.endsWith('s') && SHAPE_TYPES.includes(w.slice(0, -1))) {
    return w.slice(0, -1);
  }
  return null;
}

/**
 * Strip angles / percents / grid sizes so counts are not confused with degrees.
 */
function quantitySearchText(userCommand) {
  return stripNonCountNumbers(userCommand);
}

/**
 * Parse "five", "5 cubes", "add 3 …" as a count.
 * Ignores angles ("90°"), percents, and grid sizes ("3x3") so they are never
 * mistaken for quantity (e.g. "torus rotated 90°" must stay quantity 1).
 *
 * For multi-type commands ("2 cubes and 3 cones"), prefer
 * parseQuantitiesByObjectType — this returns only the first count.
 */
export function parseQuantityFromText(userCommand) {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return null;

  const stripped = quantitySearchText(text);

  // Explicit: "add 5 …" / "create 12 …"
  const afterVerb = stripped.match(
    /\b(?:add|create|make|place|put|spawn|insert)\s+(\d{1,3})\b/
  );
  if (afterVerb) {
    const n = parseInt(afterVerb[1], 10);
    if (Number.isFinite(n) && n >= 1) return Math.min(n, 100);
  }

  // "5 blue cubes" / "5 cylinders" / "5 objects"
  const beforeShape = stripped.match(
    new RegExp(
      `\\b(\\d{1,3})\\s+(?:[a-z-]+\\s+){0,4}(?:${COUNTABLE_SHAPE_WORDS})\\b`,
      'i'
    )
  );
  if (beforeShape) {
    const n = parseInt(beforeShape[1], 10);
    if (Number.isFinite(n) && n >= 1) return Math.min(n, 100);
  }

  // Word quantities near a shape: "five blue cylinders"
  for (const [word, n] of Object.entries(WORD_QUANTITIES)) {
    if (word === 'a' || word === 'an') continue;
    if (
      new RegExp(
        `\\b${word}\\s+(?:[a-z-]+\\s+){0,4}(?:${COUNTABLE_SHAPE_WORDS})\\b`,
        'i'
      ).test(stripped)
    ) {
      return n;
    }
    // "add five …" without relying on shape word order edge cases
    if (
      new RegExp(`\\b(?:add|create|make|place|put)\\s+${word}\\b`, 'i').test(
        stripped
      )
    ) {
      return n;
    }
  }

  // Singular article → not a multi-add
  if (/\b(?:add|create|make|place|put)\s+(?:a|an)\b/.test(stripped)) {
    return null;
  }

  return null;
}

/**
 * Per-type counts for compound adds: "2 cubes … and 3 cones …" → { cube: 2, cone: 3 }.
 */
export function parseQuantitiesByObjectType(userCommand) {
  const stripped = quantitySearchText(userCommand);
  if (!stripped.trim()) return {};

  const result = {};
  const wordNums = Object.keys(WORD_QUANTITIES)
    .filter((w) => w !== 'a' && w !== 'an')
    .join('|');
  // Adjectives between the count and the shape, but never across a
  // conjunction — otherwise "two spheres and four cylinders" reads as
  // "two … cylinders".
  const filler = `(?:(?!\\b(?:and|or|plus|with|then)\\b)[a-z-]+\\s+){0,4}`;

  const numRe = new RegExp(
    `\\b(\\d{1,3})\\s+${filler}(${OBJECT_TYPE_SHAPE_ALT})\\b`,
    'gi'
  );
  for (const m of stripped.matchAll(numRe)) {
    const n = parseInt(m[1], 10);
    const type = singularizeShapeWord(m[2]);
    if (type && Number.isFinite(n) && n >= 1) {
      result[type] = Math.min(n, 100);
    }
  }

  const wordRe = new RegExp(
    `\\b(${wordNums})\\s+${filler}(${OBJECT_TYPE_SHAPE_ALT})\\b`,
    'gi'
  );
  for (const m of stripped.matchAll(wordRe)) {
    const n = WORD_QUANTITIES[m[1].toLowerCase()];
    const type = singularizeShapeWord(m[2]);
    if (type && result[type] == null && n >= 1) result[type] = n;
  }

  // "… and a cone" → one cone
  const articleRe = new RegExp(
    `\\b(?:a|an)\\s+${filler}(${OBJECT_TYPE_SHAPE_ALT})\\b`,
    'gi'
  );
  for (const m of stripped.matchAll(articleRe)) {
    const type = singularizeShapeWord(m[1]);
    if (type && result[type] == null) result[type] = 1;
  }

  return result;
}

/**
 * Detect how multiple copies should be arranged.
 * CRITICAL: quantity > 1 always gets a layout (default: line on X)
 * so objects never pile on the same origin.
 *
 * Orientation is resolved independently so phrases like
 * "straight line vertically" correctly use the Y axis.
 */
export function parseGroupLayout(userCommand, quantityHint = 1) {
  const text = String(userCommand || '').toLowerCase();
  const qtyByType = parseQuantitiesByObjectType(text);
  const fromText = parseQuantityFromText(text);
  // Multi-type commands ("2 cubes and 3 cones"): trust the caller's per-type hint
  const quantity =
    Object.keys(qtyByType).length > 1 &&
    Number.isFinite(quantityHint) &&
    quantityHint >= 1
      ? quantityHint
      : fromText || (Number.isFinite(quantityHint) ? quantityHint : 1);

  let layout = null;
  let spacing = 1.5;
  let cols = null;

  // Stack / on top of each other (vertical pile)
  if (
    /\bstack(?:ed|s)?\b/.test(text) ||
    /\bon\s+top\s+of\s+(?:each\s+other|one\s+another)\b/.test(text)
  ) {
    layout = 'stack';
  } else if (/\b(?:in\s+a\s+)?(?:circle|ring)\b|\barranged\s+around\b/.test(text)) {
    layout = 'circle';
  } else if (/\bgrid\b|\bmatrix\b|\brows?\s+(?:and|&)\s+columns?\b/.test(text)) {
    layout = 'grid';
    const dim = text.match(/\b(\d+)\s*[x×]\s*(\d+)\b/);
    if (dim) cols = parseInt(dim[2], 10);
  } else if (
    /\b(straight\s+)?lines?\b/.test(text) ||
    /\bin\s+a\s+row\b/.test(text) ||
    /\bin\s+a\s+column\b/.test(text) ||
    /\bside\s+by\s+side\b/.test(text) ||
    /\bhorizontal(?:ly)?\b/.test(text) ||
    /\bvertical(?:ly)?\b/.test(text) ||
    /\bcolumns?\b/.test(text) ||
    /\bone\s+above\s+(?:the\s+)?other\b/.test(text) ||
    /\bspaced\b|\bspread(?:\s+out)?\b|\bapart\b|\bseparate(?:ly)?\b/.test(text)
  ) {
    layout = 'line';
    if (/\bspaced\b|\bspread(?:\s+out)?\b|\bapart\b|\bseparate(?:ly)?\b/.test(text)) {
      spacing = 2.5;
    }
  }

  // Default: never pile quantity>1 at the same point
  if (!layout && quantity > 1) {
    layout = 'line';
  }

  if (!layout) return null;

  const axis = detectOrientationAxis(text, layout) || 'x';

  return {
    layout,
    axis,
    spacing,
    cols,
    quantity:
      quantity > 1 ? quantity : quantityHint > 1 ? quantityHint : quantity,
  };
}

/**
 * Resolve line/stack axis from orientation words in the command.
 * Returns null when the command has no clear orientation cue.
 *
 * Priority: vertical/Y > depth/Z > horizontal/X
 * so "straight line vertically" → y (not x).
 */
export function detectOrientationAxis(userCommand, layout = 'line') {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return null;

  if (layout === 'stack') return 'y';
  if (layout === 'circle' || layout === 'grid') return null;

  // Vertical / up
  if (
    /\bvertical(?:ly)?\b/.test(text) ||
    /\bupwards?\b/.test(text) ||
    /\bgoing\s+up\b/.test(text) ||
    /\bcolumns?\b/.test(text) ||
    /\bin\s+a\s+column\b/.test(text) ||
    /\bone\s+above\s+(?:the\s+)?other\b/.test(text) ||
    /\balong\s+(?:the\s+)?y(?:[\s-]?axis)?\b/.test(text) ||
    /\by[\s-]?axis\b/.test(text) ||
    /\btop\s+to\s+bottom\b/.test(text) ||
    /\bbottom\s+to\s+top\b/.test(text)
  ) {
    return 'y';
  }

  // Depth / front-back
  if (
    /\balong\s+(?:the\s+)?z(?:[\s-]?axis)?\b/.test(text) ||
    /\bz[\s-]?axis\b/.test(text) ||
    /\bfront\s+to\s+back\b/.test(text) ||
    /\bback\s+to\s+front\b/.test(text) ||
    /\binto\s+the\s+(?:scene|distance|depth)\b/.test(text) ||
    /\bdepth\b/.test(text)
  ) {
    return 'z';
  }

  // Horizontal / left-right
  if (
    /\bhorizontal(?:ly)?\b/.test(text) ||
    /\bside\s+by\s+side\b/.test(text) ||
    /\bin\s+a\s+row\b/.test(text) ||
    /\bleft\s+to\s+right\b/.test(text) ||
    /\bright\s+to\s+left\b/.test(text) ||
    /\balong\s+(?:the\s+)?x(?:[\s-]?axis)?\b/.test(text) ||
    /\bx[\s-]?axis\b/.test(text)
  ) {
    return 'x';
  }

  return null;
}

export function normalizeGroupLayout(value) {
  const v = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  const map = {
    line: 'line',
    row: 'line',
    straight_line: 'line',
    straight: 'line',
    column: 'line',
    stack: 'stack',
    stacked: 'stack',
    circle: 'circle',
    ring: 'circle',
    grid: 'grid',
    matrix: 'grid',
    spread: 'line',
    spaced: 'line',
  };
  return map[v] || null;
}

/**
 * World positions for N objects under a layout, centered on origin.
 */
export function computeGroupPositions(count, layoutConfig = {}, origin = null) {
  const n = Math.max(1, Math.floor(Number(count) || 1));
  const layout = layoutConfig.layout || 'line';
  const axis = layoutConfig.axis === 'y' || layoutConfig.axis === 'z'
    ? layoutConfig.axis
    : 'x';
  const spacing = Number(layoutConfig.spacing);
  const gap = Number.isFinite(spacing) && spacing > 0 ? spacing : 1.5;
  const base = {
    x: Number(origin?.x) || 0,
    y: Number(origin?.y) || 0,
    z: Number(origin?.z) || 0,
  };

  const positions = [];

  if (layout === 'stack') {
    for (let i = 0; i < n; i++) {
      positions.push({
        x: base.x,
        y: base.y + i * gap,
        z: base.z,
      });
    }
    return positions;
  }

  if (layout === 'circle') {
    const radius = Math.max(gap, (n * gap) / (2 * Math.PI));
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      positions.push({
        x: base.x + Math.cos(a) * radius,
        y: base.y,
        z: base.z + Math.sin(a) * radius,
      });
    }
    return positions;
  }

  if (layout === 'grid') {
    const cols = Math.max(
      1,
      Math.floor(Number(layoutConfig.cols) || Math.ceil(Math.sqrt(n)))
    );
    const rows = Math.ceil(n / cols);
    for (let i = 0; i < n; i++) {
      const row = Math.floor(i / cols);
      const col = i % cols;
      positions.push({
        x: base.x + (col - (cols - 1) / 2) * gap,
        y: base.y,
        z: base.z + (row - (rows - 1) / 2) * gap,
      });
    }
    return positions;
  }

  // line / row (default)
  const start = -((n - 1) * gap) / 2;
  for (let i = 0; i < n; i++) {
    const offset = start + i * gap;
    const p = { x: base.x, y: base.y, z: base.z };
    p[axis] = (p[axis] || 0) + offset;
    positions.push(p);
  }
  return positions;
}

/**
 * Prefer spacing from the real object size so shapes don't overlap.
 * Uses height for vertical lines/stacks, width/depth for horizontal lines.
 */
export function spacingFromObject(
  object,
  layout = 'line',
  fallback = 1.5,
  axis = 'x'
) {
  if (!object) return fallback;
  try {
    object.updateMatrixWorld?.(true);
    const box = new THREE.Box3().setFromObject(object);
    if (box.isEmpty()) return fallback;
    const size = box.getSize(new THREE.Vector3());
    let dim;
    if (layout === 'stack' || axis === 'y') {
      dim = size.y;
    } else if (axis === 'z') {
      dim = size.z;
    } else {
      dim = Math.max(size.x, size.z);
    }
    const computed = dim + 0.45;
    return Number.isFinite(computed) && computed > 0.25 ? computed : fallback;
  } catch {
    return fallback;
  }
}

/**
 * "one is blue… the other is red…" — distinct individuals, not N identical copies.
 */
export function isIndividuatedMultiAdd(userCommand) {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return false;
  return (
    /\b(?:one|first)\s+is\b/.test(text) ||
    /\b(?:the\s+)?other(?:\s+one)?\s+is\b/.test(text) ||
    /\bone\s+.+\band\s+(?:the\s+)?other\b/.test(text) ||
    /\bfirst\s+.+\bsecond\b/.test(text) ||
    /\bone\s+\w+\s+and\s+(?:the\s+)?other\s+\w+/.test(text) ||
    /\beach\s+(?:a\s+)?different\b/.test(text) ||
    /\bdifferent\s+colou?rs?\b/.test(text)
  );
}

function extractColorFromSnippet(snippet) {
  const text = String(snippet || '').toLowerCase();
  const named = {
    red: '#FF0000',
    blue: '#0000FF',
    green: '#00FF00',
    yellow: '#FFFF00',
    orange: '#FFA500',
    purple: '#800080',
    violet: '#8A2BE2',
    pink: '#FFC0CB',
    cyan: '#00FFFF',
    white: '#FFFFFF',
    black: '#000000',
    gray: '#808080',
    grey: '#808080',
    brown: '#A52A2A',
    navy: '#000080',
    teal: '#008080',
    darkpurple: '#4B0082',
    darkblue: '#00008B',
    darkred: '#8B0000',
    darkgreen: '#006400',
    skyblue: '#87CEEB',
  };
  const keys = Object.keys(named).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (new RegExp(`\\b${key}\\b`, 'i').test(text)) return named[key];
  }
  const hex = text.match(/#([0-9a-f]{3}|[0-9a-f]{6})\b/i);
  return hex ? `#${hex[1]}` : null;
}

function extractRotationFromSnippet(snippet) {
  const text = String(snippet || '').toLowerCase();

  // pitch / yaw / roll
  const pitch = text.match(/\bpitch\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i);
  if (pitch) return { x: parseFloat(pitch[1]) };
  const yaw = text.match(/\byaw\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i);
  if (yaw) return { y: parseFloat(yaw[1]) };
  const roll = text.match(/\broll\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i);
  if (roll) return { z: parseFloat(roll[1]) };

  // Explicit single axis
  const withAxis = text.match(
    /(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)?\s*(?:on|around|about|along)\s*(?:the\s+)?([xyz])(?:[\s-]?axis)?/i
  );
  if (withAxis) {
    return { [withAxis[2].toLowerCase()]: parseFloat(withAxis[1]) };
  }
  const axisFirst = text.match(
    /\b([xyz])(?:[\s-]?axis)?\s*(?:rotation|rotated|rot)\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i
  );
  if (axisFirst) {
    return { [axisFirst[1].toLowerCase()]: parseFloat(axisFirst[2]) };
  }
  const rotAxis = text.match(
    /\b(?:rotation|rotated|rot)\s+[:=]?\s*([xyz])\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i
  );
  if (rotAxis) {
    return { [rotAxis[1].toLowerCase()]: parseFloat(rotAxis[2]) };
  }

  // Bare angle → all three axes
  const bare = text.match(
    /(?:rotat(?:ed|e|ion)?\s*[:=]?\s*)?(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)/i
  );
  if (bare) {
    const angle = parseFloat(bare[1]);
    return { x: angle, y: angle, z: angle };
  }

  return null;
}

/**
 * Parse "add two cubes, one is blue -45deg, other is red 45deg"
 * into distinct per-object specs.
 */
export function parseIndividuatedObjectVariants(userCommand) {
  const original = String(userCommand || '').trim();
  if (!original || !isIndividuatedMultiAdd(original)) return null;

  const text = original.toLowerCase();
  const qty = parseQuantityFromText(original) || 2;

  let objectType = null;
  for (const shape of SHAPE_TYPES) {
    if (new RegExp(`\\b${shape}s?\\b`, 'i').test(text)) {
      objectType = shape;
      break;
    }
  }
  if (!objectType) return null;

  const parts = original.split(
    /\b(?:and\s+)?(?:the\s+)?other(?:\s+one)?(?:\s+is)?\b/i
  );
  let snippets = [];
  if (parts.length >= 2) {
    const first = parts[0].replace(/^[\s\S]*?\b(?:one|first)\s+is\b/i, '');
    snippets = [first, parts.slice(1).join(' ')]
      .map((s) => s.trim())
      .filter(Boolean);
  } else {
    const ones = [
      ...original.matchAll(
        /\b(?:one|first|second)\s+(?:is\s+)?([^]+?)(?=\band\s+(?:the\s+)?(?:other|one|second)\b|$)/gi
      ),
    ];
    snippets = ones.map((m) => m[1].trim()).filter(Boolean);
  }

  if (snippets.length < 2) return null;

  const variants = snippets
    .slice(0, Math.max(qty, snippets.length))
    .map((snippet) => {
      const color = extractColorFromSnippet(snippet);
      const rotation = extractRotationFromSnippet(snippet);
      const params = { objectType, quantity: 1 };
      if (color) params.color = color;
      if (rotation) params.rotation = { x: 0, y: 0, z: 0, ...rotation };
      return params;
    });

  if (variants.length < 2) return null;
  return { objectType, quantity: qty, variants };
}

/**
 * Parse compound adds that name several shapes with their own counts and
 * properties: "add 2 dark blue cubes and 3 dark purple cones, all the cubes
 * rotated 45° and all the cones rotated 90°".
 *
 * Returns specs in the order the shapes appear, or null when the command
 * does not describe at least two distinct counted shapes.
 */
export function parseMultiTypeAddSpecs(userCommand) {
  const original = String(userCommand || '').trim();
  if (!original) return null;
  if (!/\b(?:add|create|make|place|put|spawn|insert)\b/i.test(original)) {
    return null;
  }
  if (isIndividuatedMultiAdd(original)) return null;
  if (parseSpatialRelation(original)) return null;

  const counts = parseQuantitiesByObjectType(original);
  const types = Object.keys(counts);
  if (types.length < 2) return null;

  const ordered = types
    .map((type) => ({
      type,
      index: original
        .toLowerCase()
        .search(new RegExp(`\\b${type === 'torus' ? '(?:torus|tori)' : type}`)),
    }))
    .sort((a, b) => a.index - b.index);

  return ordered.map(({ type }) => {
    const snippet = getCommandSnippetForObjectType(original, type);
    const spec = { objectType: type, quantity: counts[type] };
    const color = inferColorFromCommand(snippet);
    if (color) spec.color = color;
    const rotation = parseRotationFromText(snippet);
    if (rotation) spec.rotation = { x: 0, y: 0, z: 0, ...rotation };
    return spec;
  });
}

/**
 * Rebuild add_object calls for compound multi-shape commands so each shape
 * keeps its own count/color/rotation. The AI often copies the first count
 * onto every shape, so the parsed command text wins here.
 */
export function enrichMultiTypeAddToolCalls(userCommand, toolCalls) {
  if (!Array.isArray(toolCalls) || !toolCalls.length) return toolCalls;

  const specs = parseMultiTypeAddSpecs(userCommand);
  if (!specs || specs.length < 2) return toolCalls;

  const specTypes = new Set(specs.map((s) => s.objectType));
  const untouched = toolCalls.filter((call) => {
    if (!call || call.name !== 'add_object') return true;
    const type = String(call.parameters?.objectType || '')
      .toLowerCase()
      .trim();
    return !specTypes.has(type);
  });

  const rebuilt = specs.map((spec) => {
    const existing = toolCalls.find(
      (call) =>
        call?.name === 'add_object' &&
        String(call.parameters?.objectType || '')
          .toLowerCase()
          .trim() === spec.objectType
    );
    const params = { ...(existing?.parameters || {}), ...spec };
    if (params.quantity > 1 && !params.relativeTo) {
      const layout = parseGroupLayout(userCommand, params.quantity);
      params.layout = normalizeGroupLayout(params.layout) || layout?.layout || 'line';
      params.layoutAxis = params.layoutAxis || layout?.axis || 'x';
      if (params.spacing == null && layout?.spacing != null) {
        params.spacing = layout.spacing;
      }
      if (layout?.cols && params.layoutCols == null) {
        params.layoutCols = layout.cols;
      }
      // Keep groups from piling on the same origin as the other shape group
      delete params.position;
    }
    return { name: 'add_object', parameters: params };
  });

  // Offset each group along Z so the shape groups do not intersect
  const groups = rebuilt.length;
  rebuilt.forEach((call, i) => {
    if (call.parameters.relativeTo || call.parameters.position) return;
    call.parameters.position = {
      x: 0,
      y: 0,
      z: (i - (groups - 1) / 2) * 3,
    };
  });

  return [...rebuilt, ...untouched];
}

/**
 * Fix / rebuild tool calls for individuated multi-adds so we never
 * create qty×variants objects (e.g. 2 calls × quantity 2 = 4).
 */
export function enrichIndividuatedObjectToolCalls(userCommand, toolCalls) {
  if (!Array.isArray(toolCalls) || !toolCalls.length) return toolCalls;

  const cmd = String(userCommand || '');
  const parsed = parseIndividuatedObjectVariants(cmd);
  const individuated = !!parsed || isIndividuatedMultiAdd(cmd);
  if (!individuated) return toolCalls;

  const addCalls = toolCalls.filter((c) => c?.name === 'add_object');
  const otherCalls = toolCalls.filter((c) => c?.name !== 'add_object');

  if (parsed?.variants?.length >= 2) {
    const rebuilt = parsed.variants.map((params, i) => ({
      name: 'add_object',
      parameters: {
        ...params,
        quantity: 1,
        position: {
          x: (i - (parsed.variants.length - 1) / 2) * 1.5,
          y: 0,
          z: 0,
        },
      },
    }));
    return [...rebuilt, ...otherCalls];
  }

  if (addCalls.length > 1) {
    const fixed = addCalls.map((call, i) => {
      const params = { ...(call.parameters || {}), quantity: 1 };
      delete params.layout;
      delete params.layoutAxis;
      delete params.spacing;
      delete params.layoutCols;
      if (!params.position) {
        params.position = {
          x: (i - (addCalls.length - 1) / 2) * 1.5,
          y: 0,
          z: 0,
        };
      }
      return { ...call, parameters: params };
    });
    return [...fixed, ...otherCalls];
  }

  return toolCalls;
}

/**
 * Inject layout + quantity into add_object calls from the user command.
 * Command orientation words always win over a wrong AI layoutAxis
 * (e.g. "straight line vertically" → axis y, not x).
 * Never treats angle numbers (90°) as quantity.
 *
 * CRITICAL: For individuated adds ("one is blue, other is red") or when
 * multiple add_object calls already exist for the same type, do NOT apply
 * the total count to every call (that would create count × calls objects).
 */
export function enrichGroupLayoutToolCalls(userCommand, toolCalls) {
  if (!Array.isArray(toolCalls) || !toolCalls.length) return toolCalls;

  const cmd = String(userCommand || '');
  const qtyFromText = parseQuantityFromText(cmd);
  const qtyByType = parseQuantitiesByObjectType(cmd);
  const multiTypeCounts = Object.keys(qtyByType).length > 1;
  const orientationFromCommand = detectOrientationAxis(cmd);
  const angleValues = [
    ...cmd.matchAll(/(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)/gi),
  ].map((m) => Math.round(Math.abs(parseFloat(m[1]))));
  const singularAdd = /\b(?:add|create|make|place|put)\s+(?:a|an)\b/i.test(cmd);
  const individuated = isIndividuatedMultiAdd(cmd);

  const addTypeCounts = {};
  toolCalls.forEach((c) => {
    if (c?.name !== 'add_object') return;
    const t = String(c.parameters?.objectType || '')
      .toLowerCase()
      .trim();
    if (!t) return;
    addTypeCounts[t] = (addTypeCounts[t] || 0) + 1;
  });
  const multiTypeAdds = Object.keys(addTypeCounts).length > 1;

  return toolCalls.map((call) => {
    if (!call || call.name !== 'add_object') return call;

    const params = { ...(call.parameters || {}) };
    const typeKey = String(params.objectType || '')
      .toLowerCase()
      .trim();
    const siblingAdds = addTypeCounts[typeKey] || 1;
    let quantity = Number(params.quantity);
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;

    // Distinct individuals already represented as separate tool calls
    if (individuated || siblingAdds > 1) {
      if (quantity > 1 && (individuated || quantity === qtyFromText)) {
        quantity = 1;
        params.quantity = 1;
        delete params.layout;
        delete params.layoutAxis;
        delete params.spacing;
        delete params.layoutCols;
      }
      return { ...call, parameters: params };
    }

    const typeQty = qtyByType[typeKey];
    // Per-type counts for "2 cubes and 3 cones" — never stamp the first
    // number onto every add_object call.
    if (typeQty != null && typeQty >= 1) {
      quantity = typeQty;
      params.quantity = typeQty;
    } else if (
      !multiTypeCounts &&
      !multiTypeAdds &&
      qtyFromText &&
      qtyFromText > 1
    ) {
      quantity = qtyFromText;
      params.quantity = qtyFromText;
    } else if (
      quantity > 1 &&
      (singularAdd ||
        (!qtyFromText &&
          !typeQty &&
          angleValues.includes(Math.round(quantity))))
    ) {
      quantity = 1;
      params.quantity = 1;
      delete params.layout;
      delete params.layoutAxis;
      delete params.spacing;
      delete params.layoutCols;
    }

    const parsed = parseGroupLayout(cmd, quantity);
    if (!parsed) return { ...call, parameters: params };

    if (
      parsed.quantity > 1 &&
      ((typeQty != null && typeQty > 1) ||
        (!multiTypeCounts && qtyFromText && qtyFromText > 1))
    ) {
      params.quantity = parsed.quantity;
      quantity = parsed.quantity;
    }

    if (quantity > 1) {
      if (!params.layout) {
        params.layout = parsed.layout || 'line';
      } else {
        params.layout =
          normalizeGroupLayout(params.layout) || params.layout;
      }

      if (orientationFromCommand) {
        params.layoutAxis = orientationFromCommand;
        if (
          orientationFromCommand === 'y' &&
          (params.layout === 'line' || !params.layout)
        ) {
          params.layout = params.layout || 'line';
        }
      } else if (!params.layoutAxis && parsed.axis) {
        params.layoutAxis = parsed.axis;
      } else if (!params.layoutAxis) {
        params.layoutAxis = 'x';
      }

      if (params.spacing == null && parsed.spacing != null) {
        params.spacing = parsed.spacing;
      }
      if (parsed.cols && params.layoutCols == null) {
        params.layoutCols = parsed.cols;
      }
    }

    return { ...call, parameters: params };
  });
}
